import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../lib/api';
import { subscribeToStation } from '../../lib/realtime';

/**
 * The room's applause meter: one number everybody is pushing up together.
 *
 * ## A tap never waits for the server
 *
 * It paints immediately and is added to a count. Once a second that count is
 * posted as a single request, and the reply carries where the room actually
 * stands. A request per tap would be four a second per listener to be told a
 * number that was stale before it was written.
 *
 * ## And the room is told once a second, not once a tap
 *
 * The server decides that - see `Hype::claimPush`. Fifty people tapping four
 * times a second, each event delivered to fifty subscribers, is ten thousand
 * messages a second to say a number went up. This subscribes to the one message
 * and stops thinking about it.
 *
 * ## The number only ever climbs
 *
 * `Math.max` against everything that arrives, because an applause meter that
 * goes backwards is always wrong: within a broadcast nothing is ever taken
 * away, so a smaller number can only be a message that overtook a bigger one,
 * or this tab's own taps not having reached the server yet.
 *
 * The one case where the local number legitimately runs ahead is somebody over
 * their share for the minute - `Hype` stops counting them and does not say so.
 * They keep their sparks and their own total keeps climbing; the room's does
 * not. That is the intended shape of it: the toy is uncapped, the number is
 * not, and nothing on screen accuses anybody of anything.
 */

/** How often a second's worth of taps is sent. */
const FLUSH_MS = 1000;

/** The server refuses more than this in one request, so there is no point sending it. */
const MAX_PER_FLUSH = 25;

/**
 * How far ahead of the server a burst of tapping may run before taps are
 * dropped rather than queued.
 *
 * Somebody holding a finger down for ten seconds has queued far more than the
 * room will ever accept from them, and draining that at 25 a second means their
 * meter is still climbing a minute after they stopped - which reads as lag
 * rather than as enthusiasm.
 */
const MAX_PENDING = 120;

/** The round numbers worth setting fireworks off for. */
const MILESTONE = 1000;

/**
 * How many refused flushes in a row are still worth retrying.
 *
 * One flush a second, so this is the eight seconds after pressing listen -
 * comfortably longer than a heartbeat takes to register and far shorter than a
 * broadcast that has ended.
 */
const GRACE = 8;

export interface RoomHype {
  /** Where the room stands. */
  total: number;
  /** Taps per second across the room, as of the last message. */
  rate: number;
  /** The most recent thousand crossed while you were watching, or null. */
  milestone: number | null;
  /** Whether the room is going hard enough for the venue to know about it. */
  busy: boolean;
  /** One tap. Call it as often as somebody presses. */
  tap: () => void;
}

/**
 * How hard the room is going, and how to join in.
 *
 * `joined` is whether the viewer is actually in the room: the server refuses a
 * tap from anybody who is not, so nothing is queued for somebody who is only
 * looking.
 */
export function useHype(stationId: number, username: string, joined: boolean): RoomHype {
  const [total, setTotal] = useState(0);
  const [rate, setRate] = useState(0);
  const [milestone, setMilestone] = useState<number | null>(null);

  /** Taps waiting to be sent. A ref, because a tap must not re-render anything. */
  const pending = useRef(0);

  /**
   * The total again, readable without a render.
   *
   * A tap has to know what the number was a moment ago to know whether it just
   * crossed a thousand, and reading that inside a `setState` updater would put
   * a side effect somewhere React is allowed to run twice.
   */
  const current = useRef(0);

  /**
   * How many flushes in a row the room has refused.
   *
   * Almost always one thing: the moment after pressing listen, before the
   * heartbeat has told the server anybody is here. Taps in that window used to
   * be dropped, so the first second of enthusiasm - which is exactly when
   * somebody taps hardest - counted for nothing.
   */
  const refused = useRef(0);

  /**
   * Whether a starting number has been applied.
   *
   * Somebody arriving at 4,200 must not be shown four celebrations for
   * thousands they were not in the room for - a milestone is a moment shared
   * with whoever was there, not a receipt.
   */
  const started = useRef(false);

  const apply = useCallback((next: number) => {
    const prev = current.current;

    if (next <= prev) return;

    /*
     * Every route to a bigger number goes through here - the socket, a flush's
     * reply, and the viewer's own taps - so a thousand crossed by your own
     * finger is as much of a moment as one crossed by the room. The first
     * version only celebrated the server's numbers, which meant the person who
     * actually pushed it over was the one who saw nothing.
     */
    if (started.current && Math.floor(next / MILESTONE) > Math.floor(prev / MILESTONE)) {
      setMilestone(Math.floor(next / MILESTONE) * MILESTONE);
    }

    started.current = true;
    current.current = next;

    setTotal(next);
  }, []);

  /*
   * Where the room already was, and a backstop if it stops being told.
   *
   * The socket is the delivery mechanism: one message a second carrying the
   * total, which is how a listener who never taps sees the number move at all.
   * The poll is what happens when there is no socket - Soketi restarts, a
   * corporate network eats websockets - and without it those readers would sit
   * on the number the page loaded with for the whole broadcast.
   *
   * Twenty seconds, the same cadence the crowd uses, because this is a backstop
   * rather than the feature: one small request per reader per twenty seconds is
   * an order of magnitude below the heartbeat they are already sending.
   */
  const { data } = useQuery({
    queryKey: ['hype', username],
    queryFn: () => api.get<{ total: number; rate: number }>(
      `/users/${encodeURIComponent(username)}/live/hype`,
    ),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });

  useEffect(() => {
    if (!data) return;

    apply(data.total);
    started.current = true;
    setRate(data.rate);

  }, [data, apply]);

  useEffect(() => {
    const stop = subscribeToStation(stationId, (event, payload) => {
      if (event !== 'station.hype') return;

      const { total: sent, rate: pace } = (payload ?? {}) as { total?: number; rate?: number };

      if (typeof sent === 'number') apply(sent);
      if (typeof pace === 'number') setRate(pace);
    });

    return () => stop?.();
  }, [stationId, apply]);

  /* A celebration is a few seconds long, and then it is over. */
  useEffect(() => {
    if (milestone === null) return;

    const timer = setTimeout(() => setMilestone(null), 4000);

    return () => clearTimeout(timer);
  }, [milestone]);

  useEffect(() => {
    if (!joined) {
      pending.current = 0;

      return;
    }

    const timer = setInterval(() => {
      const sending = Math.min(pending.current, MAX_PER_FLUSH);

      if (sending === 0) return;

      pending.current -= sending;

      void api
        .post<{ total: number; counted: number }>(
          `/users/${encodeURIComponent(username)}/live/hype`,
          { count: sending },
        )
        .then((result) => {
          refused.current = 0;

          apply(result.total);
        })
        .catch(() => {
          /*
           * Put them back, for a while.
           *
           * Pressing listen and immediately tapping is the most natural thing
           * anybody does here, and for a second or two afterwards the server
           * does not yet know they are in the room - so it refuses, correctly.
           * Dropping those taps meant the meter climbed on screen and the room
           * never heard the loudest part of somebody's arrival.
           *
           * Bounded, because the other reason for a refusal is a room that has
           * gone off air, and a client that retried that forever would keep a
           * dead request on a timer for as long as the tab was open.
           */
          refused.current += 1;

          if (refused.current <= GRACE) {
            pending.current = Math.min(pending.current + sending, MAX_PENDING);
          }
        });
    }, FLUSH_MS);

    return () => clearInterval(timer);
  }, [joined, username, apply]);

  const tap = useCallback(() => {
    if (!joined) return;

    pending.current = Math.min(pending.current + 1, MAX_PENDING);

    apply(current.current + 1);
  }, [joined, apply]);

  return {
    total,
    rate,
    milestone,
    /*
     * Roughly a tap a second from a handful of people. Low enough that a small
     * room can reach it - the point is a room going off, and a room of six
     * going off is still going off - and high enough that one person idly
     * pressing does not light the place up.
     */
    busy: rate >= 1.2,
    tap,
  };
}
